/**
 * <sky-carousel> — scroll-snap carousel with prev/next buttons.
 * Markup: a `.sky-carousel__track` (or any `[data-carousel-track]`) with slides, plus optional
 * `[data-carousel-prev]` / `[data-carousel-next]` buttons inside the element.
 */
class SkyCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track], .sky-carousel__track');
    if (!this.track) return;

    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));

    this.update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      this.prev?.toggleAttribute('disabled', scrollLeft <= 2);
      this.next?.toggleAttribute('disabled', scrollLeft + clientWidth >= scrollWidth - 2);
    };
    this.track.addEventListener('scroll', this.update, { passive: true });
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.track);
    this.update();

    this.setupDrag();
  }

  /** Click-and-drag to scroll on desktop (touch/trackpad already scroll natively). */
  setupDrag() {
    const track = this.track;
    const THRESHOLD = 5;
    let pressed = false;
    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    let pointerId = null;

    const onDown = (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return; // let touch scroll natively
      if (e.button !== 0) return;
      pressed = true;
      dragging = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      // NOTE: do NOT capture the pointer or add .is-dragging here — a plain click
      // must reach the card's buttons/links. We only start dragging once the mouse
      // actually moves past the threshold (below).
    };
    const onMove = (e) => {
      if (!pressed) return;
      const dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD) return; // still a click, leave the card interactive
        dragging = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(pointerId); } catch (_) {}
      }
      track.scrollLeft = startScroll - dx;
      e.preventDefault();
    };
    const onUp = () => {
      if (!pressed) return;
      pressed = false;
      if (!dragging) return; // plain click: nothing to undo, let it through
      dragging = false;
      track.classList.remove('is-dragging'); // re-enables scroll-snap -> snaps to nearest
      try { track.releasePointerCapture(pointerId); } catch (_) {}
      // swallow the click that follows a real drag so it doesn't open a product
      const stop = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
      track.addEventListener('click', stop, { capture: true, once: true });
      setTimeout(() => track.removeEventListener('click', stop, true), 60);
    };

    track.addEventListener('pointerdown', onDown);
    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onUp);
    track.addEventListener('pointercancel', onUp);
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  /** @param {number} direction */
  step(direction) {
    const slide = this.track.firstElementChild;
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const amount = slide.getBoundingClientRect().width + gap;
    this.track.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  /** @param {number} index */
  goTo(index) {
    const slide = this.track.children[index];
    if (slide instanceof HTMLElement) this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }
}

if (!customElements.get('sky-carousel')) customElements.define('sky-carousel', SkyCarousel);
